import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  MagnifyingGlassIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ClockIcon,
  StarIcon,
  TagIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { mockSuccessStories, SuccessStory } from '../../services/successStoriesData';

export default function SuccessStoriesPage() {
  const [selectedCategory, setSelectedCategory] = useState<string>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStory, setSelectedStory] = useState<SuccessStory | null>(null);

  // Get unique categories
  const categories = ['All', ...Array.from(new Set(mockSuccessStories.map(s => s.creatorCategory)))];

  // Filter stories
  const filteredStories = mockSuccessStories.filter((story) => {
    const matchesCategory = selectedCategory === 'All' || story.creatorCategory === selectedCategory;
    const matchesSearch =
      searchQuery === '' ||
      story.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      story.creatorName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      story.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      story.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()));

    return matchesCategory && matchesSearch;
  });

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  };

  const formatCurrency = (num: number) => {
    return `$${(num / 1000).toFixed(1)}K`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <StarIcon className="h-16 w-16 mx-auto mb-4" />
            <h1 className="text-5xl font-bold mb-4">Creator Success Stories</h1>
            <p className="text-xl opacity-90 max-w-3xl mx-auto mb-8">
              Real creators. Real journeys. Discover how creators like you have built thriving communities and turned their passions into sustainable careers.
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-6 w-6 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search success stories..."
                  className="w-full pl-14 pr-4 py-4 rounded-lg text-gray-900 text-lg focus:outline-none focus:ring-2 focus:ring-white shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl font-bold text-green-600">2.5M+</div>
              <div className="text-sm text-gray-600 mt-1">Active Creators</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-blue-600">$500M+</div>
              <div className="text-sm text-gray-600 mt-1">Earned by Creators</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600">12M+</div>
              <div className="text-sm text-gray-600 mt-1">Active Patrons</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-orange-600">95%</div>
              <div className="text-sm text-gray-600 mt-1">Creator Satisfaction</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Filter */}
        <div className="mb-8">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  selectedCategory === category
                    ? 'bg-green-600 text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Stories Grid */}
        {filteredStories.length === 0 ? (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <p className="text-gray-600 text-lg">No stories found matching your criteria.</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedCategory('All');
              }}
              className="mt-4 text-green-600 hover:text-green-700 font-medium"
            >
              Clear filters
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredStories.map((story) => (
              <div
                key={story.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => setSelectedStory(story)}
              >
                {/* Featured Image */}
                {story.featuredImage && (
                  <div className="relative h-48">
                    <img
                      src={story.featuredImage}
                      alt={story.title}
                      className="w-full h-full object-cover"
                    />
                    {story.isFeatured && (
                      <div className="absolute top-4 right-4 bg-yellow-400 text-yellow-900 px-3 py-1 rounded-full text-xs font-bold flex items-center">
                        <StarIcon className="h-4 w-4 mr-1" />
                        Featured
                      </div>
                    )}
                  </div>
                )}

                <div className="p-6">
                  {/* Creator Info */}
                  <div className="flex items-center mb-4">
                    <img
                      src={story.creatorAvatar || '/default-avatar.png'}
                      alt={story.creatorName}
                      className="h-12 w-12 rounded-full mr-3"
                    />
                    <div>
                      <h3 className="font-semibold text-gray-900">{story.creatorName}</h3>
                      <p className="text-sm text-gray-600">{story.creatorCategory}</p>
                    </div>
                  </div>

                  {/* Title & Excerpt */}
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{story.title}</h4>
                  <p className="text-gray-600 text-sm mb-4 line-clamp-3">{story.excerpt}</p>

                  {/* Metrics Grid */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-green-50 rounded-lg p-3">
                      <div className="flex items-center text-green-600 mb-1">
                        <UserGroupIcon className="h-4 w-4 mr-1" />
                        <span className="text-xs font-medium">Patrons</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatNumber(story.metrics.patronCount)}
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-lg p-3">
                      <div className="flex items-center text-blue-600 mb-1">
                        <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                        <span className="text-xs font-medium">Monthly</span>
                      </div>
                      <div className="text-lg font-bold text-gray-900">
                        {formatCurrency(story.metrics.monthlyRevenue)}
                      </div>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {story.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                      >
                        #{tag}
                      </span>
                    ))}
                  </div>

                  {/* Read More Button */}
                  <button className="w-full bg-gradient-to-r from-green-600 to-blue-600 text-white py-2 rounded-lg font-medium hover:from-green-700 hover:to-blue-700 transition-colors flex items-center justify-center">
                    Read Full Story
                    <ChevronRightIcon className="h-5 w-5 ml-2" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-16 bg-gradient-to-r from-green-600 to-blue-600 rounded-lg p-12 text-center text-white">
          <h2 className="text-3xl font-bold mb-4">Ready to Write Your Success Story?</h2>
          <p className="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
            Join thousands of creators who are building sustainable careers doing what they love.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/register"
              className="bg-white text-green-600 px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors"
            >
              Start Your Journey
            </Link>
            <Link
              to="/features"
              className="bg-green-700 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-800 transition-colors"
            >
              Explore Features
            </Link>
          </div>
        </div>
      </div>

      {/* Story Modal */}
      {selectedStory && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto"
          onClick={() => setSelectedStory(null)}
        >
          <div
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            {selectedStory.featuredImage && (
              <div className="relative h-64">
                <img
                  src={selectedStory.featuredImage}
                  alt={selectedStory.title}
                  className="w-full h-full object-cover"
                />
                {selectedStory.isFeatured && (
                  <div className="absolute top-4 right-4 bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full text-sm font-bold flex items-center">
                    <StarIcon className="h-5 w-5 mr-1" />
                    Featured Story
                  </div>
                )}
              </div>
            )}

            <div className="p-8">
              {/* Creator Info */}
              <div className="flex items-center mb-6">
                <img
                  src={selectedStory.creatorAvatar || '/default-avatar.png'}
                  alt={selectedStory.creatorName}
                  className="h-16 w-16 rounded-full mr-4"
                />
                <div>
                  <h3 className="text-2xl font-bold text-gray-900">
                    {selectedStory.creatorName}
                  </h3>
                  <p className="text-gray-600">{selectedStory.creatorCategory}</p>
                  <p className="text-sm text-gray-500">@{selectedStory.creatorUsername}</p>
                </div>
              </div>

              {/* Title */}
              <h1 className="text-4xl font-bold text-gray-900 mb-6">{selectedStory.title}</h1>

              {/* Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <div className="bg-green-50 rounded-lg p-4 text-center">
                  <UserGroupIcon className="h-8 w-8 text-green-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">
                    {formatNumber(selectedStory.metrics.patronCount)}
                  </div>
                  <div className="text-sm text-gray-600">Patrons</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-4 text-center">
                  <CurrencyDollarIcon className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">
                    {formatCurrency(selectedStory.metrics.monthlyRevenue)}
                  </div>
                  <div className="text-sm text-gray-600">Monthly Revenue</div>
                </div>
                <div className="bg-purple-50 rounded-lg p-4 text-center">
                  <DocumentTextIcon className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">
                    {selectedStory.metrics.postsCreated}
                  </div>
                  <div className="text-sm text-gray-600">Posts Created</div>
                </div>
                <div className="bg-orange-50 rounded-lg p-4 text-center">
                  <ClockIcon className="h-8 w-8 text-orange-600 mx-auto mb-2" />
                  <div className="text-2xl font-bold text-gray-900">
                    {selectedStory.metrics.yearsActive}
                  </div>
                  <div className="text-sm text-gray-600">Years Active</div>
                </div>
              </div>

              {/* Story Content */}
              <div className="prose prose-lg max-w-none mb-8">
                <div className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {selectedStory.story}
                </div>
              </div>

              {/* Quote */}
              <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-lg p-6 mb-8 border-l-4 border-green-600">
                <p className="text-xl italic text-gray-800">"{selectedStory.quote}"</p>
                <p className="text-right text-gray-600 mt-2">- {selectedStory.creatorName}</p>
              </div>

              {/* Tags */}
              <div className="flex items-center flex-wrap gap-2 mb-6">
                <TagIcon className="h-5 w-5 text-gray-400" />
                {selectedStory.tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to={`/${selectedStory.creatorUsername}`}
                  className="flex-1 bg-gradient-to-r from-green-600 to-blue-600 text-white text-center py-3 rounded-lg font-semibold hover:from-green-700 hover:to-blue-700 transition-colors"
                >
                  Visit {selectedStory.creatorName}'s Page
                </Link>
                <button
                  onClick={() => setSelectedStory(null)}
                  className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-300 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
